import React, { useState } from 'react';
import CtaLink from '../Global/CtaLink';
import Button from '../Global/Button';
//@ts-ignore
import * as styles from './guides-and-manuals.module.css';
import {
    CTAConfiguration,
    KontentIcon,
    KontentRichText,
    KontentStringValue,
    ProductFamily,
} from '../../../types/KontentProps';
import RichText from '../Global/RichText';
import {
    getIcon,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import { GuidesAndManualsProduct } from '../ProductSupportLayout/productSupportTypes';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import PlainTextElement from '../Global/Headlines';
import { mapDocumentGroups } from '../ProductSupportLayout/productSupportUtils';

interface Props extends CTAConfiguration {
    kontentItemId?: string;
    families: string[];
    products: GuidesAndManualsProduct[];
    alternate_cta_copy?: KontentRichText;
    download_icon?: KontentIcon;
    filter_cta_label?: KontentStringValue;
    product_support_page_cta_label?: KontentStringValue;
}

const GuidesAndManuals: React.FC<Props> = ({
    families,
    products,
    alternate_cta_copy,
    download_icon,
    filter_cta_label,
    product_support_page_cta_label,
    kontentItemId,
    ...props
}) => {
    const dlIcon = getIcon(download_icon);
    const defaultFilterString = 'ALL';
    const bp = useBreakpoint();
    const [selectedFamily, setSelectedFamily] = useState<string>('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [filterString, setFilterString] = useState(defaultFilterString);
    const [results, setResults] = useState(products);

    const filterResults = () => {
        if (selectedProduct) {
            setResults(
                products.filter((product) => product.name === selectedProduct)
            );
            setFilterString(`${selectedFamily} - ${selectedProduct}`);
        } else if (selectedFamily) {
            setResults(
                products.filter((product) => product.family === selectedFamily)
            );
            setFilterString(`${selectedFamily}`);
        } else {
            setResults(products);
            setFilterString(defaultFilterString);
        }
    };

    const handleChangeFamily = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setSelectedFamily(event.currentTarget.value);
        setSelectedProduct('');
    };
    return (
        <section
            className={styles.section}
            data-kontent-item-id={kontentItemId}
        >
            <div className={styles.filters}>
                <div className={styles.filter}>
                    <label htmlFor="product-family">Product Family</label>
                    <div className="select-wrapper select-wrapper--small">
                        <select
                            id="product-family"
                            className={styles.productSelect}
                            value={selectedFamily}
                            onChange={handleChangeFamily}
                        >
                            <option value="">ALL</option>
                            {families?.map((family, idx) => (
                                <option
                                    value={family}
                                    key={`fam${family}-${idx}`}
                                >
                                    {family}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.filter}>
                    <label htmlFor="product">Product</label>
                    <div className="select-wrapper select-wrapper--small">
                        <select
                            id="product"
                            className={styles.productSelect}
                            value={selectedProduct}
                            onChange={(e) =>
                                setSelectedProduct(e.currentTarget.value)
                            }
                        >
                            <option value="">ALL</option>
                            {products?.map((product, prodIdx) => {
                                if (product.family === selectedFamily) {
                                    return (
                                        <option
                                            value={product.name}
                                            key={`prodopt${product.name}${prodIdx}`}
                                        >
                                            {product.name}
                                        </option>
                                    );
                                }
                            })}
                        </select>
                    </div>
                </div>
            </div>
            <div className={styles.filterActions}>
                <Button
                    type="button"
                    onClick={filterResults}
                    visualStyle="outlineDark"
                >
                    {filter_cta_label?.value}
                </Button>
            </div>
            {alternate_cta_copy?.value !== '<p><br></p>' && (
                <div>
                    {RichText(
                        alternate_cta_copy,
                        false,
                        undefined,
                        undefined,
                        'alternate_cta_copy'
                    )}
                </div>
            )}
            <p className={styles.filterShowing}>SHOWING: {filterString}</p>
            <div>
                {results?.map((result, resIdx) => {
                    const url = result.image.url;
                    const ownersManuals = result.documents?.filter(
                        (doc) => doc.typeCode === 'owner_manual'
                    );
                    const otherDocs = result.documents?.filter(
                        (doc) => doc.typeCode !== 'owner_manual'
                    );
                    const mappedDocumentGroups = mapDocumentGroups(
                        result.documentGroups || []
                    );
                    return (
                        <div
                            className={styles.result}
                            key={`res${result.name}${resIdx}`}
                        >
                            {!bp.md && (
                                <h3>
                                    <PlainTextElement
                                        text={{ value: result.name }}
                                        elementCodename="name"
                                    />
                                </h3>
                            )}
                            <div
                                className={styles.resultImage}
                                data-kontent-element-codename="gallery_images"
                            >
                                <LazyImage
                                    src={optimizeKontentImageUrl(url, {
                                        ...imagePreset.card.desktop,
                                        q: 95,
                                    })}
                                    srcSet={buildKontentSrcSet(
                                        url,
                                        imagePreset.card.desktop.w,
                                        {
                                            fm: 'webp' as const,
                                            q: 95,
                                        }
                                    )}
                                    sizes="(max-width: 576px) 100vw, 25vw"
                                    alt={result.image.description || ''}
                                    {...getDisplayDimensions(
                                        result.image.width,
                                        result.image.height,
                                        imagePreset.card.desktop.w
                                    )}
                                />
                            </div>
                            <div className={styles.resultContent}>
                                {bp.md && (
                                    <h3>
                                        <PlainTextElement
                                            text={{ value: result.name }}
                                            elementCodename="name"
                                        />
                                    </h3>
                                )}
                                <div className={styles.documents}>
                                    <div className={styles.manuals}>
                                        {ownersManuals.length > 0 ? (
                                            ownersManuals?.map(
                                                (document, idx) => {
                                                    const url =
                                                        document?.image?.url;
                                                    return (
                                                        <div
                                                            className={`${styles.manual} ${styles.manualBig}`}
                                                            key={`manual${document.url}`}
                                                        >
                                                            <div
                                                                className={
                                                                    styles.manualImageWrapper
                                                                }
                                                                data-kontent-element-codename="image"
                                                            >
                                                                {!bp.md && (
                                                                    <h4>
                                                                        {
                                                                            document.name
                                                                        }
                                                                    </h4>
                                                                )}
                                                                <LazyImage
                                                                    className={
                                                                        styles.manualImage
                                                                    }
                                                                    src={optimizeKontentImageUrl(
                                                                        url,
                                                                        {
                                                                            ...imagePreset
                                                                                .card
                                                                                .desktop,
                                                                            q: 95,
                                                                        }
                                                                    )}
                                                                    srcSet={buildKontentSrcSet(
                                                                        url,
                                                                        imagePreset
                                                                            .card
                                                                            .desktop
                                                                            .w,
                                                                        {
                                                                            fm: 'webp' as const,
                                                                            q: 95,
                                                                        }
                                                                    )}
                                                                    sizes="(max-width: 576px) 100vw, 25vw"
                                                                    alt={
                                                                        document
                                                                            ?.image
                                                                            ?.description ||
                                                                        ''
                                                                    }
                                                                    {...getDisplayDimensions(
                                                                        document
                                                                            ?.image
                                                                            ?.width,
                                                                        document
                                                                            ?.image
                                                                            ?.height,
                                                                        imagePreset
                                                                            .card
                                                                            .desktop
                                                                            .w
                                                                    )}
                                                                />
                                                                <a
                                                                    href={
                                                                        document.url
                                                                    }
                                                                    rel="nofollow"
                                                                >
                                                                    <LazyImage
                                                                        className={
                                                                            styles.dlIcon
                                                                        }
                                                                        src={
                                                                            dlIcon?.url as string
                                                                        }
                                                                        alt={
                                                                            dlIcon?.description ||
                                                                            ''
                                                                        }
                                                                        // SVG icon shown at 1rem
                                                                        width={
                                                                            16
                                                                        }
                                                                        height={
                                                                            16
                                                                        }
                                                                    />{' '}
                                                                    Download PDF
                                                                </a>
                                                            </div>
                                                            <div
                                                                className={
                                                                    styles.manualInfo
                                                                }
                                                            >
                                                                {bp.md && (
                                                                    <h4>
                                                                        {
                                                                            document.name
                                                                        }
                                                                    </h4>
                                                                )}
                                                                {document?.pdfEmbeddedLinks &&
                                                                    RichText(
                                                                        document.pdfEmbeddedLinks,
                                                                        undefined,
                                                                        undefined,
                                                                        undefined,
                                                                        'pdfEmbeddedLinks'
                                                                    )}
                                                                {result
                                                                    .supportPage
                                                                    .url && (
                                                                    <div
                                                                        className={
                                                                            styles.supportCtaDesktop
                                                                        }
                                                                    >
                                                                        <CtaLink
                                                                            href={
                                                                                result
                                                                                    .supportPage
                                                                                    .url
                                                                            }
                                                                        >
                                                                            {
                                                                                product_support_page_cta_label?.value
                                                                            }
                                                                        </CtaLink>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            )
                                        ) : (
                                            <div
                                                className={`${styles.manual} ${styles.manualBig}`}
                                            >
                                                <div
                                                    className={
                                                        styles.manualInfo
                                                    }
                                                >
                                                    {result.supportPage.url && (
                                                        <div
                                                            className={
                                                                styles.supportCtaDesktop
                                                            }
                                                        >
                                                            <CtaLink
                                                                href={
                                                                    result
                                                                        .supportPage
                                                                        .url
                                                                }
                                                            >
                                                                {
                                                                    product_support_page_cta_label?.value
                                                                }
                                                            </CtaLink>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {otherDocs?.length > 0 && (
                                        <div className={styles.otherDocs}>
                                            <h4>Other Documentation</h4>
                                            {otherDocs?.map((document, id) => {
                                                return (
                                                    <div
                                                        className={
                                                            styles.manual
                                                        }
                                                        key={`other${document.url}${id}`}
                                                    >
                                                        <div
                                                            className={
                                                                styles.manualInfo
                                                            }
                                                        >
                                                            <h5>
                                                                {document.name}
                                                            </h5>
                                                            <a
                                                                href={
                                                                    document.url
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer nofollow"
                                                            >
                                                                <LazyImage
                                                                    className={
                                                                        styles.dlIcon
                                                                    }
                                                                    src={
                                                                        dlIcon?.url as string
                                                                    }
                                                                    alt={
                                                                        dlIcon?.description ||
                                                                        ''
                                                                    }
                                                                    // SVG icon shown at 1rem
                                                                    width={16}
                                                                    height={16}
                                                                />{' '}
                                                                Download PDF
                                                            </a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {mappedDocumentGroups?.length >
                                                0 && (
                                                <>
                                                    {mappedDocumentGroups.map(
                                                        (group, id) => (
                                                            <details
                                                                key={`group-${id}`}
                                                                className={
                                                                    styles.docGroup
                                                                }
                                                            >
                                                                <summary
                                                                    className={
                                                                        styles.docGroupTitle
                                                                    }
                                                                >
                                                                    {group.name}
                                                                </summary>
                                                                <ul
                                                                    className={
                                                                        styles.docGroupList
                                                                    }
                                                                >
                                                                    {group.documents.map(
                                                                        (
                                                                            document,
                                                                            id
                                                                        ) => (
                                                                            <li
                                                                                key={
                                                                                    'doc' +
                                                                                    document.url +
                                                                                    id
                                                                                }
                                                                            >
                                                                                <div
                                                                                    className={
                                                                                        styles.manual
                                                                                    }
                                                                                    key={`other${document.url}${id}`}
                                                                                >
                                                                                    <div
                                                                                        className={
                                                                                            styles.manualInfo
                                                                                        }
                                                                                    >
                                                                                        <h5>
                                                                                            {
                                                                                                document.name
                                                                                            }
                                                                                        </h5>
                                                                                        <a
                                                                                            href={
                                                                                                document.url
                                                                                            }
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer nofollow"
                                                                                        >
                                                                                            <LazyImage
                                                                                                className={
                                                                                                    styles.dlIcon
                                                                                                }
                                                                                                src={
                                                                                                    dlIcon?.url as string
                                                                                                }
                                                                                                alt={
                                                                                                    dlIcon?.description ||
                                                                                                    ''
                                                                                                }
                                                                                                // SVG icon shown at 1rem
                                                                                                width={
                                                                                                    16
                                                                                                }
                                                                                                height={
                                                                                                    16
                                                                                                }
                                                                                            />{' '}
                                                                                            Download
                                                                                            PDF
                                                                                        </a>
                                                                                    </div>
                                                                                </div>
                                                                            </li>
                                                                        )
                                                                    )}
                                                                </ul>
                                                            </details>
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <div className={styles.supportCtaMobile}>
                                        <CtaLink href={result.supportPage.url}>
                                            {
                                                product_support_page_cta_label?.value
                                            }
                                        </CtaLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default GuidesAndManuals;
